import { useEffect, useState } from "react";
import { getNameErrors } from "../helpers/helpers.tsx";
import apiFetch, { getCountries, getHeadquarters } from "../helpers/requests.tsx";
import { useTranslation } from "react-i18next";

const ChangeHospitalDataModal = ({
  setShowChangeDataModal,
  hospitalId,
  hospitalName,
  hospitalCountryId,
  hospitalCountryCode,
  headquarterId,
  showSelectHQ = false,
  handleChangeData,
}) => {
  const [newHospitalName, setNewHospitalName] = useState("");
  const [newHospitalCountryId, setNewHospitalCountryId] = useState("");
  const [newHospitalCountryCode, setNewHospitalCountryCode] = useState("");
  const [newHeadquarterId, setNewHeadquarterId] = useState("");
  const [newHeadquarterName, setNewHeadquarterName] = useState("");

  const [saveDataSubmitted, setSaveDataSubmitted] = useState(false);

  const [countries, setCountries] = useState<any[]>([]);
  const [headquarters, setHeadquarters] = useState<any[]>([]);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    setNewHospitalName(hospitalName);
    setNewHospitalCountryId(hospitalCountryId);
    setNewHospitalCountryCode(hospitalCountryCode);
    setNewHeadquarterId(headquarterId);

    const fetchData = async () => {
      const countryList = await getCountries();
      countryList.sort((a: any, b: any) =>
        t("country_" + a.code).localeCompare(t("country_" + b.code))
      );
      setCountries(countryList);

      const headquarterList = await getHeadquarters();
      setHeadquarters(headquarterList);
    };

    fetchData();
  }, [hospitalName, hospitalCountryId, hospitalCountryCode, headquarterId]);

  const updateHospitalData = async (
    hospitalId: string | null,
    hospitalName: string,
    hospitalCountryId: string,
    headquarterId: string
  ) => {
    try {
      await apiFetch(`/updateGroupData`, {
        method: "POST",
        body: JSON.stringify({
          groupId: hospitalId, // Mapping hospitalId to groupId for backend compatibility
          groupName: hospitalName,
          groupCountryId: hospitalCountryId,
          headquarterId,
        }),
      });
    } catch (error) {
      console.error("Error update hospital data:", error);
      throw new Error("An error occurred while update hospital data");
    }
  };

  const handleSaveClick = async () => {
    setSaveDataSubmitted(true);

    if (getNameErrors(newHospitalName, t)) return;

    await updateHospitalData(
      hospitalId,
      newHospitalName,
      newHospitalCountryId,
      newHeadquarterId
    );

    handleChangeData(
      newHospitalName,
      newHospitalCountryId,
      newHospitalCountryCode,
      newHeadquarterId,
      newHeadquarterName
    );
  };

  return (
    <div className="confirmation-modal">
      <div className="modal-content">
        <div style={{ textAlign: "center", fontSize: "1.3em" }}>
          {t("Edit_Hospital")}
        </div>
        <div className="mb-3" style={{ marginTop: "2vh" }}>
          <label
            htmlFor="nameInput"
            style={{
              marginLeft: "20px",
              color: "#404040",
              fontWeight: "bold",
            }}
          >
            {t("Hospital_Name")}
          </label>
          <input
            type="text"
            id="nameInput"
            className="form-control"
            value={newHospitalName}
            onChange={(e) => {
              setNewHospitalName(e.target.value);
              setSaveDataSubmitted(false);
            }}
            style={{
              backgroundColor: "#CCCCCC",
              borderRadius: "20px",
              color: "#404040",
              height: "5vh",
              maxHeight: "40px",
              fontSize: "1em",
              paddingLeft: "20px",
            }}
          />
          <label
            htmlFor="nameInput"
            style={{
              marginLeft: "20px",
              color: "#A63939",
              fontSize: "0.8em",
              visibility:
                saveDataSubmitted && getNameErrors(newHospitalName, t)
                  ? "visible"
                  : "hidden",
            }}
          >
            {getNameErrors(newHospitalName, t)}
          </label>
        </div>

        <div className="mb-3" style={{ marginTop: "2vh" }}>
          <label
            htmlFor="countrySelect"
            style={{
              marginLeft: "20px",
              color: "#404040",
              fontWeight: "bold",
            }}
          >
            {t("Country")}
          </label>
          <select
            id="countrySelect"
            className="form-control"
            style={{
              backgroundColor: "#CCCCCC",
              borderRadius: "20px",
              color: "#404040",
              height: "5vh",
              maxHeight: "40px",
              fontSize: "1em",
              paddingLeft: "20px",
            }}
            value={newHospitalCountryId}
            onChange={(e) => {
              const selectedCountryId = e.target.value;
              const selectedCountry = countries.find(
                (country) => country.id === selectedCountryId
              );
              if (selectedCountry) {
                setNewHospitalCountryId(selectedCountryId);
                setNewHospitalCountryCode(selectedCountry.code);
                setSaveDataSubmitted(false);
              }
            }}
          >
            {countries.map((country: any) => (
              <option key={country.id} value={country.id}>
                {t("country_" + country.code)}
              </option>
            ))}
          </select>
        </div>

        {showSelectHQ && (
          <div className="mb-3" style={{ marginTop: "2vh" }}>
            <label
              htmlFor="headquarterSelect"
              style={{
                marginLeft: "20px",
                color: "#404040",
                fontWeight: "bold",
              }}
            >
              {t("Headquarter")}
            </label>
            <select
              id="headquarterSelect"
              className="form-control"
              style={{
                backgroundColor: "#CCCCCC",
                borderRadius: "20px",
                color: "#404040",
                height: "5vh",
                maxHeight: "40px",
                fontSize: "1em",
                paddingLeft: "20px",
              }}
              value={newHeadquarterId}
              onChange={(e) => {
                const selectedHeadquarterId = e.target.value;
                const selectedHeadquarter = headquarters.find(
                  (headquarter) => headquarter.id === selectedHeadquarterId
                );

                if (selectedHeadquarter || selectedHeadquarterId === "") {
                  setNewHeadquarterId(selectedHeadquarterId);
                  setNewHeadquarterName(
                    selectedHeadquarterId === "" ? "" : selectedHeadquarter.name
                  );
                  setSaveDataSubmitted(false);
                }
              }}
            >
              <option value=""></option>
              {headquarters.map((headquarter: any) => (
                <option key={headquarter.id} value={headquarter.id}>
                  {headquarter.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="button-container">
          <button
            type="button"
            className="buttonCancel btn btn-primary"
            onClick={() => {
              setShowChangeDataModal(false);
              setSaveDataSubmitted(false);
            }}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary buttonAcceptGreen"
            onClick={() => handleSaveClick()}
          >
            {t("Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeHospitalDataModal;

